import React from 'react';
import { Flex } from '@adobe/react-spectrum';
import './Algorithm.css';
import { Helmet } from 'react-helmet';
import { BreadCrumbs } from '../breadcrumbs/BreadCrumbs';

export function Algorithm() {
    const helmetName = 'Power Level Algorithm';
    const helmetCanonical = 'https://www.commandersalt.com/algorithm';
    const helmetDescription = 'Power Level Algorithm';
    const helmetTitle = 'Commandersalt.com - Power Level Algorithm';

    return (
        <Flex direction="column">
            <Helmet>
                <title>{helmetName}</title>
                <meta name="creator" content="Ben Rimbey" />
                <link rel="canonical" href={helmetCanonical} />
                <meta name="description" content={helmetDescription} />
                <meta name="twitter:title" content={helmetTitle} />
                <meta property="og:site_name" content="Commandersalt.com" />
                <meta property="og:title" content={helmetTitle} />
                <meta property="og:image" content="https://www.commandersalt.com/chef-kiss-meta-image.png" />
                <meta property="og:description" content={helmetDescription} />
            </Helmet>
            <BreadCrumbs />

            <div className="algorithmRoot">
                <section className="algorithmHero panelGradient">
                    <div className="algorithmHeroMain">
                        <span className="algorithmHeroEyebrow">Scoring Algorithm</span>
                        <h1 className="algorithmHeroTitle">How the score actually gets built</h1>
                        <p className="algorithmHeroSubtitle">
                            Most people picture the power scale as a straight line from 1 to 10. It isn’t. Think of it
                            as a cubic curve — big jumps down low, a long flat plateau in the middle, and a steep
                            ramp once you get past 7 on the way toward cEDH. That shape is what the site’s ingestion
                            pipeline is modeled on.
                        </p>
                    </div>
                    <aside className="algorithmTldr">
                        <span className="algorithmTldrLabel">TL;DR</span>
                        <p className="algorithmTldrBody">
                            Everything is <strong>not</strong> a 7.
                        </p>
                    </aside>
                </section>

                <section className="algorithmCard panelGradient">
                    <header className="algorithmCardHeader">
                        <span className="algorithmCardEyebrow">Shape of the curve</span>
                        <h2 className="algorithmCardTitle">Power level vs. perceived strength</h2>
                        <p className="algorithmCardSubtitle">
                            Precons and low-power brews cluster low; everything 7-and-up gets squeezed into a narrow
                            cEDH-adjacent region. The compression is deliberate — it keeps cEDH tiers legible instead
                            of everything jamming up against 10.
                        </p>
                    </header>
                    <figure className="algorithmFigure">
                        <img
                            className="algorithmFigureImage"
                            src="resources/power_level_graph.png"
                            alt="Cubic power-level curve used by the site"
                        />
                    </figure>
                </section>

                <section className="algorithmCard panelGradient">
                    <header className="algorithmCardHeader">
                        <span className="algorithmCardEyebrow">Ingestion Flow</span>
                        <h2 className="algorithmCardTitle">What happens when a deck is submitted</h2>
                        <p className="algorithmCardSubtitle">
                            The diagram below walks the full ingestion pipeline — parsing, tagging, salt lookup,
                            bracket resolution, synergy scoring, and final compression into the power number you see
                            on a deck page.
                        </p>
                    </header>
                    <div className="algorithmFlowchartFrame">
                        <iframe
                            title="Ingestion flowchart"
                            className="algorithmFlowchartIframe"
                            src="resources/ingestion_flowchart.html"
                        />
                    </div>
                </section>

                <aside className="algorithmLivingDoc">
                    <span className="algorithmLivingDocBadge">Living Document</span>
                    <p className="algorithmLivingDocBody">
                        The algorithm keeps evolving as the meta shifts and the data set grows. Expect weightings and
                        thresholds to move; the shape of the curve is the part that stays put.
                    </p>
                </aside>
            </div>
        </Flex>
    );
}
